/**
 * Dashboard Page - Main overview dashboard
 *
 * Shows:
 * - Key performance metrics (Cash Collected, Revenue, Close Rate)
 * - Call statistics (Show Rate, Calls Due/Taken/Closed)
 * - Average metrics (Cash per Call, Close, Application, Opt-in)
 * - Additional stats (New Cash, MRR, Applications, Opt-ins)
 * - Charts (Closes by Country, Lead Source, Content Performance)
 * - Recent calls table
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton, FilterBar } from "../components";
import { AdditionalStatCardSkeleton } from "../components/StatCard";
import { branding } from "../config/branding";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { useTheme } from "../context/ThemeContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ResponsivePie } from "@nivo/pie";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { EditRowDialog, type FieldDefinition } from "../components/EditRowDialog";

// Icons
const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TargetIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// Chart colors from branding config
const CHART_COLORS = branding.colors.chart;

// Extended colors for pie charts
const PIE_COLORS = [
  "#3B82F6", // blue
  "#EF4444", // red
  "#10B981", // green
  "#F59E0B", // amber
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#F97316", // orange
  "#84CC16", // lime
  "#6366F1", // indigo
  "#14B8A6", // teal
];

// Red shades palette for crimson theme
const CRIMSON_CHART_COLORS = [
  "#ef4444", // red-500
  "#dc2626", // red-600
  "#b91c1c", // red-700
  "#991b1b", // red-800
  "#7f1d1d", // red-900
  "#f87171", // red-400
  "#fca5a5", // red-300
  "#c53030", // deep red
  "#e53e3e", // mid red
  "#fc8181", // red-300 lighter
  "#be2626", // alt red-700
];

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatCurrencyAbbreviated = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return formatCurrency(value);
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatYAxis = (value: number) => {
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toString();
};

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  totalApplicationsCash: number;
  totalOptinsCash: number;
  newCashCollected: number;
  mrrCash: number;
  totalGrossRevenue: number;
  totalApplications: number;
  totalOptins: number;
  cancelledCalls: number;
  cancelRate: number;
}

interface ChartDataItem {
  name: string;
  value: number;
  percentage: number;
  revenue?: number;
}

interface TrendPoint {
  date: string;
  value: number;
}

interface RecentCall {
  id: string;
  _id: string;
  timestamp: string;
  dateCallWasTaken: string;
  leadName: string;
  email: string;
  phone: string;
  closer: string;
  setter: string;
  status: "closed" | "no-show" | "pending" | "not-interested";
  callOutcome: string;
  cashCollected: number;
  notes: string;
  leadSourcePlatform?: string;
  leadSourceContent?: string;
}

// Field definitions for editing calls
const CALL_EDIT_FIELDS: FieldDefinition[] = [
  { key: "leadName", label: "Lead Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "closer", label: "Closer", type: "text" },
  { key: "setter", label: "Setter", type: "text" },
  { key: "callOutcome", label: "Call Outcome", type: "text" },
  { key: "notes", label: "Notes", type: "textarea" },
];

interface FilterOption {
  value: string;
  label: string;
}

interface ContentOption extends FilterOption {
  allValues?: string[]; // Multiple video IDs that map to the same title
}

interface FilterOptions {
  closers: string[];
  setters: string[];
  funnels: FilterOption[]; // Now has value/label for normalized display
  contents: ContentOption[];
  countries: string[];
  sources: string[];
}

// Nivo pie chart common theme
const getNivoPieTheme = () => ({
  labels: {
    text: {
      fontSize: 12,
      fontWeight: 400,
    },
  },
});

// Custom tooltip component for Nivo pie charts (works in dark/light mode)
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: datum.color }}
      />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

// Convert chart data to Nivo format
// First deduplicate by name (merge items with same name), then assign colors
const toNivoPieData = (data: ChartDataItem[], colors: string[]) => {
  // Merge items with the same name
  const mergedMap = new Map<string, { value: number; revenue: number }>();
  for (const item of data) {
    const existing = mergedMap.get(item.name);
    if (existing) {
      existing.value += item.value;
      existing.revenue += item.revenue || 0;
    } else {
      mergedMap.set(item.name, { value: item.value, revenue: item.revenue || 0 });
    }
  }

  // Convert back to array and calculate percentages
  const merged = Array.from(mergedMap.entries()).map(([name, data]) => ({
    name,
    value: data.value,
    revenue: data.revenue,
  }));

  // Sort by value descending, then by name alphabetically for stable ordering
  // This prevents items with the same value from jumping around on re-renders
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.name.localeCompare(b.name);
  });

  // Calculate total for percentages
  const total = merged.reduce((sum, item) => sum + item.value, 0);

  return merged.map((item, index) => ({
    id: item.name, // Name is now unique after merging
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
};

// ─── KPI Bar ─────────────────────────────────────────────────────────────────
type KpiPeriod = "month" | "90days" | "year";

const KPI_PERIOD_LABELS: Record<KpiPeriod, string> = {
  month: "This Month",
  "90days": "90 Days",
  year: "This Year",
};

function KPIBar({ isAdmin, currentCash }: { isAdmin: boolean; currentCash: number }) {
  const [period, setPeriod] = useState<KpiPeriod>("month");
  const [target, setTarget] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchKpi = useCallback(async (p: KpiPeriod) => {
    try {
      const res = await fetch(`/api/kpi?period=${p}`);
      const json = await res.json() as { success: boolean; data?: { target: number } };
      if (json.success && json.data) {
        setTarget(json.data.target);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchKpi(period); }, [period, fetchKpi]);

  const saveTarget = async () => {
    const val = parseFloat(editValue.replace(/[^0-9.]/g, ""));
    if (isNaN(val) || val < 0) return;
    setSaving(true);
    try {
      await fetch("/api/kpi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ period, target: val }),
      });
      setTarget(val);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const pct = target > 0 ? Math.min((currentCash / target) * 100, 100) : 0;
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(2)}M`
      : n >= 1_000
      ? `$${(n / 1_000).toFixed(1)}K`
      : `$${Math.round(n)}`;

  const barGlowColor = pct < 33 ? "#ef4444" : pct < 60 ? "#f97316" : "#22c55e";
  const pctTextColor = pct < 33 ? "#ef4444" : pct < 60 ? "#f97316" : "#22c55e";

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.4 }}
      className="relative w-full rounded-xl border border-border overflow-hidden bg-card"
    >
      {/* Ambient glow strip at top */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, var(--brand-gradient-start), var(--brand-gradient-end), transparent)" }}
      />

      <div className="flex items-center gap-4 px-4 py-3">
        {/* Left: Label + Period */}
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Revenue Goal</span>
          <select
            value={period}
            onChange={(e) => { setPeriod(e.target.value as KpiPeriod); setEditing(false); }}
            className="text-sm font-bold text-card-foreground bg-transparent border-none focus:outline-none cursor-pointer appearance-none pr-4"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center" }}
          >
            {(Object.keys(KPI_PERIOD_LABELS) as KpiPeriod[]).map((p) => (
              <option key={p} value={p} className="bg-card text-card-foreground">{KPI_PERIOD_LABELS[p]}</option>
            ))}
          </select>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-border flex-shrink-0" />

        {/* Center: Progress bar + amounts */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {/* Bar track */}
          <div className="w-full h-2 rounded-full bg-muted-foreground/20 overflow-hidden">
            <motion.div
              className="h-full rounded-full relative"
              style={{
                background: "linear-gradient(90deg, #ef4444 0%, #f97316 45%, #eab308 70%, #22c55e 100%)",
                boxShadow: pct > 5 ? `0 0 8px 1px ${barGlowColor}` : "none",
              }}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
            />
          </div>

          {/* Amount labels */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground tabular-nums">
              <span className="text-card-foreground font-semibold">{fmt(currentCash)}</span>
              {target > 0 && <span className="text-muted-foreground"> / {fmt(target)}</span>}
            </span>
            {target > 0 && (
              <span
                className="text-xs font-bold tabular-nums"
                style={{ color: target > 0 ? pctTextColor : "var(--muted-foreground)" }}
              >
                {pct.toFixed(1)}%
              </span>
            )}
            {target === 0 && (
              <span className="text-xs text-muted-foreground italic">No target set</span>
            )}
          </div>
        </div>

        {/* Right: Admin edit */}
        {isAdmin && (
          <div className="flex-shrink-0">
            {editing ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-muted border border-border rounded-lg px-2 py-1">
                  <span className="text-xs text-muted-foreground font-medium">$</span>
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveTarget(); if (e.key === "Escape") setEditing(false); }}
                    placeholder="500000"
                    autoFocus
                    className="w-24 text-sm bg-transparent text-card-foreground placeholder-muted-foreground focus:outline-none font-medium"
                  />
                </div>
                <button
                  type="button"
                  onClick={saveTarget}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, var(--brand-gradient-start), var(--brand-gradient-end))" }}
                >
                  {saving ? "…" : "Save"}
                </button>
                <button type="button" onClick={() => setEditing(false)} className="text-muted-foreground hover:text-card-foreground transition-colors text-xs px-1">✕</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setEditValue(target > 0 ? String(target) : ""); setEditing(true); }}
                title="Set KPI target"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-card-foreground bg-muted hover:bg-secondary border border-border transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Set Target
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function Dashboard() {
  const { theme } = useTheme();
  const isCrimson = theme === "crimson";
  const chartColors = CRIMSON_CHART_COLORS;
  const areaStroke = "#991b1b";

  // Get current user session
  const { data: session } = useSession();

  // Use global date range from context (synced with header date picker)
  const { dateRange: globalDateRange, formatForApi } = useDateRange();

  // Use shared filters from context (synced with URL via nuqs)
  const {
    dateField,
    closer,
    setter,
    funnel,
    leadType,
    content,
    country,
    source,
    setDateField,
    setCloser,
    setSetter,
    setFunnel,
    setLeadType,
    setContent,
    setCountry,
    setSource,
    // Array setters for clearing invalid filters
    setClosers,
    setSetters,
    setFunnels,
    setContents,
    setCountries,
    setSources,
    clearAllFilters,
  } = useFilters();

  // Data states
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendPoint[]>([]);
  const [trendType, setTrendType] = useState<"daily" | "monthly">("monthly");
  const [countryData, setCountryData] = useState<ChartDataItem[]>([]);
  const [leadSourceData, setLeadSourceData] = useState<ChartDataItem[]>([]);
  const [contentData, setContentData] = useState<ChartDataItem[]>([]);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  // Server-side pagination state
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotalItems, setTableTotalItems] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
  });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Chart filter mode: "closed" shows only closed leads, "all" shows all leads
  const [chartFilterMode, setChartFilterMode] = useState<"closed" | "all">("closed");

  // Edit/Delete state for recent calls
  const [editingCall, setEditingCall] = useState<RecentCall | null>(null);
  const [deletingCall, setDeletingCall] = useState<RecentCall | null>(null);

  // Status badge helper
  const getStatusBadgeClass = (status: RecentCall["status"]) => {
    const styles: Record<string, string> = {
      closed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      "no-show": "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
      pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
      "not-interested": "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    };
    return styles[status] || styles.pending;
  };

  const getStatusLabel = (status: RecentCall["status"]) => {
    const labels: Record<string, string> = {
      closed: "Closed",
      "no-show": "No Show",
      pending: "Pending",
      "not-interested": "Not Interested",
    };
    return labels[status] || status;
  };

  // Define columns for recent calls table
  const recentCallsColumns: ColumnDef<RecentCall>[] = useMemo(() => [
    {
      id: "timestamp",
      header: "Timestamp",
      cell: (row) => {
        if (!row.timestamp) return <span className="text-muted-foreground">-</span>;
        const date = new Date(row.timestamp);
        return (
          <span className="text-muted-foreground text-xs">
            {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        );
      },
    },
    {
      id: "dateCallWasTaken",
      header: "Date Call Was Taken",
      cell: (row) => (
        row.dateCallWasTaken ? (
          <span className="text-muted-foreground">{row.dateCallWasTaken}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
    },
    {
      id: "leadName",
      header: "Lead Name",
      accessorKey: "leadName",
      className: "font-medium",
    },
    {
      id: "closer",
      header: "Closer",
      accessorKey: "closer",
    },
    {
      id: "setter",
      header: "Setter",
      accessorKey: "setter",
    },
    {
      id: "source",
      header: "Source",
      cell: (row) => {
        const platform = row.leadSourcePlatform || "";
        const content = row.leadSourceContent || "";
        if (!platform && !content) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-xs text-muted-foreground">
            {platform}{platform && content ? " | " : ""}{content}
          </span>
        );
      },
    },
    {
      id: "status",
      header: "Call Outcome",
      cell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(row.status)}`}>
          {row.callOutcome || "-"}
        </span>
      ),
    },
    {
      id: "cashCollected",
      header: "Cash Collected",
      cell: (row) => (
        row.cashCollected > 0 ? (
          <span className="text-green-600 font-medium">{formatCurrency(row.cashCollected)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
      headerClassName: "text-right",
      className: "text-right",
    },
  ], []);

  // Handle edit
  const handleEditCall = (call: RecentCall) => {
    setEditingCall(call);
  };

  // Handle save
  const handleSaveCall = async (data: Record<string, unknown>) => {
    // Map frontend field names to backend field names
    const mappedData = {
      prospectName: data.leadName,
      prospectEmail: data.email,
      prospectNumber: data.phone,
      closerName: data.closer,
      setterName: data.setter,
      callOutcome: data.callOutcome,
      callNotes: data.notes,
    };

    const response = await fetch(`/api/leads/${data._id || data.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mappedData),
    });

    if (!response.ok) {
      throw new Error("Failed to update call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle delete
  const handleDeleteCall = (call: RecentCall) => {
    setDeletingCall(call);
  };

  // Confirm delete
  const confirmDeleteCall = async () => {
    if (!deletingCall) return;

    const response = await fetch(`/api/leads/${deletingCall._id || deletingCall.id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete call");
    }

    // Refresh data
    fetchData(false);
  };

  // Fetch data from API
  // tableOnly: if true, only show table loading (for pagination changes)
  const fetchData = useCallback(async (showLoading = true, tableOnly = false) => {
    if (showLoading) {
      if (tableOnly) {
        setTableLoading(true);
      } else {
        setIsLoading(true);
      }
    }
    try {
      // Use global date range from context
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      if (dateField) params.set("dateField", dateField);
      if (closer) params.set("closer", closer);
      if (setter) params.set("setter", setter);
      if (funnel) params.set("funnel", funnel);
      if (leadType) params.set("leadType", leadType);
      if (content) params.set("content", content);
      if (country) params.set("country", country);
      if (source) params.set("source", source);
      // Chart filter: closedOnly=true shows only closed leads in charts
      params.set("closedOnly", chartFilterMode === "closed" ? "true" : "false");
      // Pagination params
      params.set("page", String(tablePage));
      params.set("pageSize", String(tablePageSize));

      const response = await fetch(`/api/dashboard/summary?${params}`);
      const result = await response.json();

      if (!result.success) throw new Error(result.error);

      const { data } = result;

      setMetrics({
        totalCashCollected: data.metrics.totalCashCollected || 0,
        totalRevenue: data.metrics.totalRevenue || 0,
        closeRate: data.metrics.closeRate || 0,
        showRate: data.metrics.showRate || 0,
        callsDue: data.metrics.callsDue || 0,
        callsTaken: data.metrics.callsTaken || 0,
        callsClosed: data.metrics.callsClosed || 0,
        avgCashPerCall: data.metrics.avgCashPerCall || 0,
        avgCashPerClose: data.metrics.avgCashPerClose || 0,
        avgCashPerApplication: data.metrics.avgCashPerApplication || 0,
        avgCashPerOptin: data.metrics.avgCashPerOptin || 0,
        totalApplicationsCash: data.metrics.totalApplicationsCash || 0,
        totalOptinsCash: data.metrics.totalOptinsCash || 0,
        newCashCollected: data.metrics.newCashCollected || 0,
        mrrCash: data.metrics.mrrCash || 0,
        totalGrossRevenue: data.metrics.totalGrossRevenue || 0,
        totalApplications: data.metrics.totalApplications || 0,
        totalOptins: data.metrics.totalOptins || 0,
        cancelledCalls: data.metrics.cancelledCalls || 0,
        cancelRate: data.metrics.cancelRate || 0,
      });

      if (data.filterOptions) {
        setFilterOptions({
          closers: data.filterOptions.closers || [],
          setters: data.filterOptions.setters || [],
          funnels: data.filterOptions.funnels || [],
          contents: data.filterOptions.contents || [],
          countries: data.filterOptions.countries || [],
          sources: data.filterOptions.sources || [],
        });
      }

      setCashTrend(data.cashTrend || []);
      setTrendType(data.trendType || "monthly");
      setCountryData(data.countryData || []);
      setLeadSourceData(data.leadSourceData || []);
      setContentData(data.contentData || []);
      setRecentCalls(data.recentCalls || []);

      // Update pagination state from server response
      if (data.pagination) {
        setTableTotalItems(data.pagination.totalItems);
        setTableTotalPages(data.pagination.totalPages);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    } finally {
      if (showLoading) {
        if (tableOnly) {
          setTableLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [formatForApi, dateField, closer, setter, funnel, leadType, content, country, source, chartFilterMode, tablePage, tablePageSize]);

  // Auto-remove invalid filters when available options change
  // (e.g., when selecting a funnel that has no data for certain closers)
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: Record<string, string[]> = {};

    // Check closer filter - if selected closer not in available options, remove it
    if (closer && filterOptions.closers.length > 0) {
      if (!filterOptions.closers.includes(closer)) {
        filtersToRemove.closers = [];
      }
    }

    // Check setter filter
    if (setter && filterOptions.setters.length > 0) {
      if (!filterOptions.setters.includes(setter)) {
        filtersToRemove.setters = [];
      }
    }

    // Check funnel filter (uses value/label format)
    if (funnel && filterOptions.funnels.length > 0) {
      const funnelValues = filterOptions.funnels.map(f => f.value);
      if (!funnelValues.includes(funnel)) {
        filtersToRemove.funnels = [];
      }
    }

    // Check content filter (uses value/label format)
    if (content && filterOptions.contents.length > 0) {
      const contentValues = filterOptions.contents.map(c => c.value);
      if (!contentValues.includes(content)) {
        filtersToRemove.contents = [];
      }
    }

    // Check country filter
    if (country && filterOptions.countries.length > 0) {
      if (!filterOptions.countries.includes(country)) {
        filtersToRemove.countries = [];
      }
    }

    // Check source filter
    if (source && filterOptions.sources.length > 0) {
      if (!filterOptions.sources.includes(source)) {
        filtersToRemove.sources = [];
      }
    }

    // Remove invalid filters
    if (Object.keys(filtersToRemove).length > 0) {
      if ('closers' in filtersToRemove) setClosers([]);
      if ('setters' in filtersToRemove) setSetters([]);
      if ('funnels' in filtersToRemove) setFunnels([]);
      if ('contents' in filtersToRemove) setContents([]);
      if ('countries' in filtersToRemove) setCountries([]);
      if ('sources' in filtersToRemove) setSources([]);
    }
  }, [filterOptions, closer, setter, funnel, content, country, source, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Track if the next fetch should be table-only (for pagination changes)
  const isPaginationChangeRef = useRef(false);

  // Reset to page 1 when filters change
  useEffect(() => {
    isPaginationChangeRef.current = false;
    setTablePage(1);
  }, [globalDateRange, dateField, closer, setter, funnel, leadType, content, country, source, chartFilterMode]);

  // Handle pagination changes - mark as pagination-only before state update
  const handlePageChange = (page: number) => {
    isPaginationChangeRef.current = true;
    setTablePage(page);
  };

  const handlePageSizeChange = (size: number) => {
    isPaginationChangeRef.current = true;
    setTablePageSize(size);
    setTablePage(1);
  };

  // Fetch data when dependencies change
  useEffect(() => {
    const isTableOnly = isPaginationChangeRef.current;
    isPaginationChangeRef.current = false; // Reset for next change
    fetchData(true, isTableOnly);
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchData(false), 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Clear all filters (date range is managed globally by the header picker)
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Chart click handlers
  const handleCountryClick = (name: string) => {
    if (name === "Other") return;
    setCountry(country === name ? "" : name);
  };

  const handleSourceClick = (name: string) => {
    if (name === "Other" || name === "N/A") return;
    setSource(source === name ? "" : name);
  };

  const handleContentClick = (name: string) => {
    if (name === "Other") return;
    // Find the matching filter option by label to get the video ID value
    const matchingOption = filterOptions.contents.find(
      (c) => c.label === name || c.label.startsWith(name) || name.startsWith(c.label)
    );
    const valueToSet = matchingOption?.value || name;
    setContent(content === valueToSet ? "" : valueToSet);
  };

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Call Statistics Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Average Metrics Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i + 4} />
          ))}
        </div>

        {/* Cash Totals Skeleton */}
        <div className="grid gap-4 grid-cols-2">
          {[0, 1].map((i) => (
            <StatCardSkeleton key={i} index={i + 8} />
          ))}
        </div>

        {/* Additional Statistics Skeleton */}
        <div className="grid gap-4 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <AdditionalStatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="relative overflow-hidden bg-card rounded-xl border border-border p-6"
        >
          <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
          <div className="h-[400px] rounded-lg bg-muted/30 animate-pulse flex items-center justify-center">
            <motion.div
              className="w-32 h-32 rounded-full border-4 border-muted border-t-muted-foreground/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
            }}
          />
        </motion.div>
      </div>
    );
  }

  // Get greeting based on time of day with user's first name
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };


  return (
    <div className="space-y-4">
      {/* Row 1: Greeting + Controls */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between gap-2"
      >
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="text-lg md:text-2xl font-bold tracking-tight flex-shrink-0"
        >
          {getGreeting()}
        </motion.h1>

        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <AnimatePresence mode="wait">
            {lastUpdated && (
              <motion.span
                key="updated"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="text-xs text-muted-foreground hidden sm:block"
              >
                Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </motion.span>
            )}
          </AnimatePresence>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${
              autoRefresh
                ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400"
                : "bg-muted text-muted-foreground"
            }`}
          >
            <motion.span
              animate={autoRefresh ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 1.5, repeat: Infinity }}
              className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${autoRefresh ? "bg-green-500" : "bg-gray-400"}`}
            />
            <span className="hidden xs:inline">{autoRefresh ? "Live" : "Paused"}</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300 }}
            onClick={() => fetchData(false)}
            className="p-1.5 md:p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
            title="Refresh now"
          >
            <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </motion.button>
        </div>
      </motion.div>

      {/* Row 2: KPI Revenue Goal Card */}
      <KPIBar isAdmin={(session?.user as { role?: string } | undefined)?.role === "admin"} currentCash={metrics?.totalCashCollected ?? 0} />

      {/* Row 3: Filters - all in one uniform line */}
      <FilterBar
        showDateFieldToggle={true}
        dateField={dateField as "timestamp" | "dateCallWasTaken"}
        onDateFieldChange={(v) => setDateField(v)}
        filters={[
          {
            key: "funnel",
            label: "All Funnels",
            options: filterOptions.funnels.map((f) => ({ value: f.value, label: f.label })),
            value: funnel,
            onChange: (v) => setFunnel(v as string),
          },
          {
            key: "leadType",
            label: "All Lead Types",
            options: [
              { value: "applicant", label: "Applicant" },
              { value: "optin", label: "Opt-in" },
            ],
            value: leadType,
            onChange: (v) => setLeadType(v as string),
          },
          {
            key: "content",
            label: "Select Content",
            options: filterOptions.contents.map((c) => ({ value: c.value, label: c.label.length > 30 ? c.label.slice(0, 30) + "..." : c.label })),
            value: content,
            onChange: (v) => setContent(v as string),
          },
          {
            key: "closer",
            label: "Select Closers",
            options: filterOptions.closers.map((c) => ({ value: c, label: c })),
            value: closer,
            onChange: (v) => setCloser(v as string),
          },
          {
            key: "setter",
            label: "Select Setters",
            options: filterOptions.setters.map((s) => ({ value: s, label: s })),
            value: setter,
            onChange: (v) => setSetter(v as string),
          },
        ]}
        onClearAll={handleClearAll}
      />

      {/* Main Metric Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <MetricCard
          label="Cash Collected"
          value={formatCurrencyAbbreviated(metrics?.totalCashCollected || 0)}
          icon={<DollarIcon />}
          color="green"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={0}
        />
        <MetricCard
          label="Revenue"
          value={formatCurrencyAbbreviated(metrics?.totalRevenue || 0)}
          icon={<TrendingUpIcon />}
          color="brand"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={1}
        />
        <MetricCard
          label="Close Rate"
          value={formatPercent(metrics?.closeRate || 0)}
          icon={<TargetIcon />}
          color="blue"
          data={[]}
          tooltipFormatter={(v) => formatPercent(v)}
          index={2}
        />
      </div>

      {/* Call Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Show Rate" value={formatPercent(metrics?.showRate || 0)} index={0} />
        <StatCard label="Calls Due" value={(metrics?.callsDue || 0).toLocaleString()} index={1} />
        <StatCard label="Calls Taken" value={(metrics?.callsTaken || 0).toLocaleString()} index={2} />
        <StatCard label="Calls Closed" value={(metrics?.callsClosed || 0).toLocaleString()} index={3} />
      </div>

      {/* Average Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Avg. Cash/Call" value={formatCurrency(metrics?.avgCashPerCall || 0)} index={4} />
        <StatCard label="Avg. Cash/Close" value={formatCurrency(metrics?.avgCashPerClose || 0)} index={5} />
        <StatCard label="Avg. Cash/Application" value={formatCurrency(metrics?.avgCashPerApplication || 0)} index={6} />
        <StatCard label="Avg. Cash/Opt-in" value={formatCurrency(metrics?.avgCashPerOptin || 0)} index={7} />
      </div>

      {/* Cash Totals */}
      <div className="grid gap-4 grid-cols-2">
        <StatCard label="Total Cash (Applications)" value={formatCurrency(metrics?.totalApplicationsCash || 0)} index={8} />
        <StatCard label="Total Cash (Opt-ins)" value={formatCurrency(metrics?.totalOptinsCash || 0)} index={9} />
      </div>

      {/* Additional Statistics */}
      <div className="grid gap-4 md:grid-cols-6">
        {[
          { label: "New Cash Collected", value: formatCurrency(metrics?.newCashCollected || 0), description: "Total cash from new deals (excludes MRR)" },
          { label: "MRR", value: formatCurrency(metrics?.mrrCash || 0), description: "Monthly recurring revenue" },
          { label: "Total Gross Revenue", value: formatCurrency(metrics?.totalGrossRevenue || 0), description: "Total contract value (all revenue)" },
          { label: "Total Applications", value: (metrics?.totalApplications || 0).toLocaleString() },
          { label: "Total Opt-ins", value: (metrics?.totalOptins || 0).toLocaleString() },
          { label: "Cancel Rate", value: formatPercent(metrics?.cancelRate || 0), description: `${(metrics?.cancelledCalls || 0).toLocaleString()} cancelled of ${(metrics?.callsDue || 0).toLocaleString()} booked` },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.4,
              delay: 0.5 + i * 0.06,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            whileHover={{
              y: -3,
              boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)",
              transition: { duration: 0.2 },
            }}
            className="bg-card rounded-xl border-2 border-border p-4 md:p-6 transition-colors duration-300 hover:border-border/80"
          >
            <motion.p
              className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 md:mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 + i * 0.06 }}
            >
              {stat.label}
            </motion.p>
            <p className="text-xl md:text-3xl font-bold">{stat.value}</p>
            {stat.description && (
              <motion.p
                className="text-xs text-muted-foreground mt-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 + i * 0.06 }}
              >
                {stat.description}
              </motion.p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Closes by Country Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {chartFilterMode === "closed" ? "Closes" : "Leads"} by Country
            </h3>
            <AnimatePresence>
              {country && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setCountry("")}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <span>Clear: {country}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[400px] cursor-pointer">
          <ResponsivePie
            data={toNivoPieData(countryData, chartColors)}
            margin={{ top: 40, right: 120, bottom: 40, left: 120 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => String(d.label)}
            arcLinkLabelsStraightLength={20}
            arcLinkLabelsDiagonalLength={20}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            onClick={(node) => handleCountryClick(String(node.label))}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Cash Collected Trend and Lead Source */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Cash Collected Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-4">
            Cash Collected Trend
            {trendType === "daily" && (
              <span className="text-xs font-normal text-muted-foreground ml-2">(Daily)</span>
            )}
          </h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashTrend} margin={{ top: 10, right: 5, left: -10, bottom: 10 }}>
                <defs>
                  <pattern
                    id="gridPattern"
                    x="0"
                    y="0"
                    width="20"
                    height="40"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 20 0 L 0 0 0 20"
                      fill="none"
                      stroke="hsl(var(--border))"
                      strokeWidth="0.5"
                      strokeOpacity="0.5"
                    />
                  </pattern>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={areaStroke} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={areaStroke} stopOpacity={0.1} />
                  </linearGradient>
                  <filter id="dotShadow" x="-100%" y="-100%" width="300%" height="300%">
                    <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.4)" />
                  </filter>
                </defs>
                <rect
                  x="32"
                  y="0"
                  width="100%"
                  height="100%"
                  fill="url(#gridPattern)"
                  style={{ pointerEvents: "none" }}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: "#a1a1aa" }}
                  tickMargin={6}
                  interval="preserveStartEnd"
                  tickFormatter={(val) => {
                    if (trendType === "monthly") {
                      const [year, month] = val.split("-");
                      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                      const monthName = monthNames[parseInt(month, 10) - 1] || month;
                      return `${monthName} ${year}`;
                    }
                    const [, month, day] = val.split("-");
                    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const monthName = monthNames[parseInt(month, 10) - 1] || month;
                    return `${monthName} ${parseInt(day, 10)}`;
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: "#a1a1aa" }}
                  tickFormatter={(val) => {
                    if (val === 0) return "$0";
                    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
                    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
                    return `$${val}`;
                  }}
                  tickMargin={2}
                  width={32}
                />
                <Tooltip
                  cursor={{ stroke: areaStroke, strokeWidth: 1 }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      let displayLabel = label;
                      if (trendType === "monthly" && typeof label === "string") {
                        const [year, month] = label.split("-");
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const monthName = monthNames[parseInt(month || "", 10) - 1] || month;
                        displayLabel = `${monthName} ${year}`;
                      } else if (typeof label === "string") {
                        const [year, month, day] = label.split("-");
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const monthName = monthNames[parseInt(month || "", 10) - 1] || month;
                        displayLabel = `${monthName} ${parseInt(day || "", 10)}, ${year}`;
                      }
                      return (
                        <div className="rounded-lg bg-card border border-border p-3 shadow-lg">
                          <div className="text-xs font-medium mb-1 text-muted-foreground">{displayLabel}</div>
                          <div className="text-xs font-medium mb-1 text-muted-foreground">Cash:</div>
                          <div className="text-sm font-semibold text-foreground">{formatCurrency(payload[0]?.value as number)}</div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={areaStroke}
                  strokeWidth={2}
                  fill="url(#areaGradient)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: areaStroke,
                    stroke: "white",
                    strokeWidth: 2,
                    filter: "url(#dotShadow)",
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Closes by Lead Source */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold">
                {chartFilterMode === "closed" ? "Closes" : "Leads"} By Lead Source
              </h3>
              <AnimatePresence>
                {source && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => setSource("")}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  >
                    <span>Clear: {source}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
            <motion.select
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              value={chartFilterMode}
              onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
              className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
            >
              <option value="closed">Closed Leads</option>
              <option value="all">All Leads</option>
            </motion.select>
          </div>
          <div className="h-[300px] cursor-pointer">
            <ResponsivePie
              data={toNivoPieData(leadSourceData, chartColors)}
              margin={{ top: 30, right: 100, bottom: 30, left: 100 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              arcLinkLabelsStraightLength={15}
              arcLinkLabelsDiagonalLength={15}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              onClick={(node) => handleSourceClick(String(node.label))}
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
            />
          </div>
        </motion.div>
      </div>

      {/* Closes by Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {chartFilterMode === "closed" ? "Closes" : "Leads"} by Content
            </h3>
            <AnimatePresence>
              {content && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setContent("")}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <span>Clear: {content.length > 20 ? content.slice(0, 20) + "..." : content}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[500px] cursor-pointer">
          <ResponsivePie
            data={toNivoPieData(contentData, chartColors)}
            margin={{ top: 40, right: 140, bottom: 40, left: 140 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={8}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => {
              const label = String(d.label);
              return label.length > 25 ? label.slice(0, 22) + "..." : label;
            }}
            arcLinkLabelsStraightLength={20}
            arcLinkLabelsDiagonalLength={20}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            onClick={(node) => handleContentClick(String(node.label))}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Recent Calls Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Recent Calls</h3>
          <p className="text-sm text-muted-foreground">Latest call activity</p>
        </div>
        <DataTable
          data={recentCalls}
          columns={recentCallsColumns}
          rowKey="id"
          emptyMessage="No recent calls found."
          loading={tableLoading}
          serverPagination={{
            currentPage: tablePage,
            pageSize: tablePageSize,
            totalItems: tableTotalItems,
            totalPages: tableTotalPages,
            pageSizeOptions: [10, 25, 50, 100],
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          onEdit={handleEditCall}
          onDelete={handleDeleteCall}
        />
      </motion.div>

      {/* Edit Dialog */}
      <EditRowDialog
        open={!!editingCall}
        onOpenChange={(open) => !open && setEditingCall(null)}
        title="Edit Call"
        description="Update call information"
        fields={CALL_EDIT_FIELDS}
        data={editingCall as Record<string, unknown> | null}
        onSave={handleSaveCall}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={!!deletingCall}
        onOpenChange={(open) => !open && setDeletingCall(null)}
        title="Delete Call"
        itemName={deletingCall?.leadName}
        onConfirm={confirmDeleteCall}
      />
    </div>
  );
}
